import { useEffect, useRef, useState } from "react";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { applyPausePunctuation, COMMA_PAUSE_MS } from "../utils/formatDictation";

// Accepts BCP-47 codes like "en", "hi", "fr-FR". Anything else (a language
// name, an id, empty) falls back so recognition never starts with an invalid tag.
const LANGUAGE_CODE = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/i;
export const toRecognitionLanguage = (language, fallback = "en-US") =>
    typeof language === "string" && LANGUAGE_CODE.test(language.trim())
        ? language.trim()
        : fallback;

/**
 * Speech-to-text with live, pause-punctuated transcript (same engine and
 * punctuation as the assignment mic, without the waveform).
 *
 * @param {{ language?: string, onFinal?: (text: string) => void }} options
 *   onFinal receives the punctuated text whenever a session ends — manual stop
 *   or the browser ending it (error, mic revoked) — so speech is never lost.
 */
const useDictation = ({ language = "en-US", onFinal } = {}) => {
    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition,
        isMicrophoneAvailable,
    } = useSpeechRecognition();

    const [isListening, setIsListening] = useState(false);
    const sessionRef = useRef(false);
    const engineStartedRef = useRef(false); // engine reported listening this session
    const pausesRef = useRef([]); // { afterWord, gapMs }
    const lastChangeRef = useRef({ time: 0, words: 0 });
    const transcriptRef = useRef("");
    const onFinalRef = useRef(onFinal);
    onFinalRef.current = onFinal;

    const punctuate = (text, options) => {
        try {
            return applyPausePunctuation(text, pausesRef.current, options);
        } catch (err) {
            console.error("Dictation punctuation failed, using raw transcript:", err);
            return (text || "").trim();
        }
    };

    // Record pauses: new words after a silent gap mark a break after the previous word
    useEffect(() => {
        const now = performance.now();
        const wordCount = transcript.trim() ? transcript.trim().split(/\s+/).length : 0;
        const last = lastChangeRef.current;
        if (sessionRef.current && last.words > 0 && wordCount > last.words) {
            const gapMs = now - last.time;
            if (gapMs >= COMMA_PAUSE_MS) {
                pausesRef.current.push({ afterWord: last.words, gapMs });
            }
        }
        lastChangeRef.current = { time: now, words: wordCount };
        transcriptRef.current = transcript;
    }, [transcript]);

    const finish = () => {
        if (!sessionRef.current) return;
        sessionRef.current = false;
        engineStartedRef.current = false;
        const text = punctuate(transcriptRef.current, { final: true });
        setIsListening(false);
        SpeechRecognition.stopListening();
        resetTranscript();
        if (text) onFinalRef.current?.(text);
    };

    // Continuous mode auto-restarts on silence, so `listening` only drops for
    // real ends (error, permission revoked, another tab took the mic)
    useEffect(() => {
        if (listening) {
            engineStartedRef.current = true;
        } else if (sessionRef.current && engineStartedRef.current) {
            finish();
        }
    }, [listening]);

    // Mic permission denied → the engine never starts; don't leave the UI "listening"
    useEffect(() => {
        if (sessionRef.current && !isMicrophoneAvailable) {
            finish();
        }
    }, [isMicrophoneAvailable]);

    // Release the mic if the page unmounts mid-dictation
    useEffect(() => {
        return () => {
            if (sessionRef.current) {
                sessionRef.current = false;
                SpeechRecognition.abortListening();
            }
        };
    }, []);

    const startListening = async () => {
        if (sessionRef.current || !browserSupportsSpeechRecognition) return false;

        resetTranscript();
        transcriptRef.current = "";
        pausesRef.current = [];
        lastChangeRef.current = { time: 0, words: 0 };
        engineStartedRef.current = false;
        sessionRef.current = true;
        setIsListening(true);

        try {
            await SpeechRecognition.startListening({
                continuous: true,
                language: toRecognitionLanguage(language),
            });
            return true;
        } catch (err) {
            console.error("Failed to start speech recognition:", err);
            sessionRef.current = false;
            setIsListening(false);
            return false;
        }
    };

    return {
        isListening,
        // Punctuated live text for the current session only
        transcript: isListening ? punctuate(transcript) : "",
        startListening,
        stopListening: finish,
        browserSupportsSpeechRecognition,
    };
};

export default useDictation;
